// Helpers of the Mail tab (components/MailPanel.vue): addresses, sizes, and the sandboxed document an HTML mail is
// shown in.
import type { MailAddress } from '../api/types';

/** `Shop <shop@example.com>`, or the bare address. */
export function addressText(a?: MailAddress | null): string {
  if (!a) return '—';
  return a.name ? `${a.name} <${a.address}>` : a.address;
}

export function addressList(as: readonly MailAddress[] | undefined): string {
  return as && as.length ? as.map(addressText).join(', ') : '—';
}

/** The sender's name if it has one, else the address: the list's first column. */
export function senderShort(a?: MailAddress | null): string {
  return a ? a.name || a.address : '—';
}

export function sizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** `2026-11-04 09:20` in the viewer's time zone. */
export function mailTime(ts?: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * The srcdoc of the frame an HTML mail is shown in. The frame is sandboxed without scripts or same-origin; this adds
 * a CSP that blocks every remote load (tracking pixels, fonts, styles) unless `remote` allows images, and opens links
 * in a new tab.
 */
export function frameDoc(html: string, remote: boolean): string {
  const img = remote ? "img-src data: https: http:" : 'img-src data:';
  const csp = `default-src 'none'; style-src 'unsafe-inline'; font-src data:; ${img}`;
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}">`
    + '<base target="_blank"><style>body{margin:16px;font-family:system-ui,sans-serif;color:#111;background:#fff}</style></head>'
    + `<body>${html}</body></html>`;
}

/** Whether the HTML loads anything from the network (the "load remote images" switch only shows then). */
export function hasRemote(html: string): boolean {
  return /\b(src|background)\s*=\s*["']?\s*https?:|url\(\s*["']?https?:/i.test(html);
}
